/**
 * The report set (piece 26, Reports; decisions 0111, 0205). 🔒 One registry:
 * the fixed list of individually named pre-built reports, in the six
 * categories, each with the filters it takes. 🔨 No saved report definition,
 * no field picker, and no report whose name or key carries the word custom —
 * the suite asserts all three, because a builder left implied surfaces as a
 * missed feature only when somebody is asked to build it.
 *
 * 🔨 A row that waits on a later piece says so in one sentence rather than
 * being left out: a report nobody can run yet and a report nobody planned
 * both read as absent otherwise.
 */
import { formatCurrency } from '../money';
import type { LedgerBasis } from '../accounting/postingMap';
import type { FiscalAnchor } from './customise';
import { BUDGETS_ENABLED_KEY } from '../settings/settingsIndex';
import { ALL_MODULES_OFF, isOptionalModuleKey, moduleRowVisible, type ModuleStates, type OptionalModuleKey } from '../settings/modules';

/** The six categories, in the order the Reports list draws them (decision 0111; Pipeline added by decision 0205). */
export const REPORT_CATEGORIES = ['FinancialStatements', 'SalesAndCustomers', 'PurchasesAndExpenses', 'InventoryAndAssets', 'Tax', 'Pipeline'] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const REPORT_CATEGORY_LABELS: Readonly<Record<ReportCategory, string>> = {
  FinancialStatements: 'Financial Statements',
  SalesAndCustomers: 'Sales and Customers',
  PurchasesAndExpenses: 'Purchases and Expenses',
  InventoryAndAssets: 'Inventory and Assets',
  Tax: 'Tax',
  Pipeline: 'Pipeline',
};

/**
 * The filters a report takes. 🔨 The list is closed: a filter is a named
 * control the report page knows how to draw, never a field a person picks.
 */
export const REPORT_FILTERS = ['dateRange', 'basis', 'asOf', 'siteId', 'taxYear', 'agingThresholdDays', 'groupBy'] as const;
export type ReportFilter = (typeof REPORT_FILTERS)[number];

export interface ReportSpec {
  key: string;
  category: ReportCategory;
  name: string;
  /** What the report says it is, one sentence, drawn under its name on the list. */
  purpose: string;
  filters: readonly ReportFilter[];
  /**
   * 🔒 The column a typed From and To cut a list report's rows by (T2784): the row's own date, where the
   * report is a list as at a day rather than a range. Absent where the report cuts its own query by the range.
   */
  rangeColumn?: string;
  /** The sentence naming the piece that makes the row runnable, where one is owed; null for a row that runs now. */
  waitsOn: string | null;
  /**
   * The optional module the row belongs to, where it belongs to one (piece 27,
   * decision 0520). 🔨 A row naming a module is listed while that module is on
   * and hidden while it is off, the same one nullable key a navigation row and
   * a Settings entry carry. Hiding is presentation: the route still answers.
   */
  moduleKey?: OptionalModuleKey | null;
}

const RANGE_AND_BASIS: readonly ReportFilter[] = ['dateRange', 'basis'];
const RANGE: readonly ReportFilter[] = ['dateRange'];
const AS_OF_AND_RANGE: readonly ReportFilter[] = ['asOf', 'dateRange'];

/**
 * 🔒 The named list, exactly as the piece names it. A report joins this list
 * in the commit that gives it a method on the reports service; a key with no
 * method is refused by name rather than answering an empty table.
 */
export const REPORTS: readonly ReportSpec[] = [
  // ---- Financial Statements ----
  { key: 'trial-balance', category: 'FinancialStatements', name: 'Trial Balance', purpose: 'Every account with a balance, debits against credits, and the two totals that must agree.', filters: RANGE_AND_BASIS, waitsOn: null },
  { key: 'profit-and-loss', category: 'FinancialStatements', name: 'Profit and Loss', purpose: 'Income and expense over the range, with the net result.', filters: RANGE_AND_BASIS, waitsOn: null },
  { key: 'balance-sheet', category: 'FinancialStatements', name: 'Balance Sheet', purpose: 'Assets, liabilities and the computed equity as at the end of the range.', filters: RANGE_AND_BASIS, waitsOn: null },
  { key: 'cash-flow', category: 'FinancialStatements', name: 'Cash Flow', purpose: 'The indirect method, each account placed by its own cash flow section.', filters: RANGE_AND_BASIS, waitsOn: null },
  { key: 'general-ledger-detail', category: 'FinancialStatements', name: 'General Ledger Detail', purpose: 'Every posted line in the range, by account, with the entry it belongs to.', filters: RANGE, waitsOn: null },
  { key: 'journal', category: 'FinancialStatements', name: 'Journal', purpose: 'Every entry in the range in posting order, each drilling through to the document behind it.', filters: RANGE, waitsOn: null },
  { key: 'bank-reconciliation', category: 'FinancialStatements', name: 'Bank Reconciliation', purpose: 'What each completed reconciliation wrote: the statement, the balance and the difference.', filters: RANGE, waitsOn: null },
  { key: 'unmatched-payments', category: 'FinancialStatements', name: 'Unmatched Payments', purpose: 'Every payment recorded with no bank line matched to it yet, oldest first, with how long it has stood open.', filters: AS_OF_AND_RANGE, rangeColumn: 'paymentDate', waitsOn: null },
  { key: 'unapplied-deposits', category: 'FinancialStatements', name: 'Unapplied Deposits', purpose: 'Every deposit the bank reported with no payment or invoice matched to it, oldest first.', filters: AS_OF_AND_RANGE, rangeColumn: 'postedOn', waitsOn: null },
  { key: 'open-clearing', category: 'FinancialStatements', name: 'Open 2050', purpose: 'What the goods-received clearing account has not netted to zero, by vendor and item.', filters: [], waitsOn: null },
  { key: 'period-reopens', category: 'FinancialStatements', name: 'Period Reopens', purpose: 'Every reopened accounting period with its reason and who reopened it.', filters: RANGE, rangeColumn: 'reopenedAt', waitsOn: null },
  {
    key: 'budget-versus-actual', category: 'FinancialStatements', name: 'Budget Versus Actual',
    purpose: 'What each account was budgeted against what it posted, with the variance and the variance as a percentage.',
    filters: ['asOf'], waitsOn: null, moduleKey: BUDGETS_ENABLED_KEY,
  },

  // ---- Sales and Customers ----
  { key: 'sales-by-customer', category: 'SalesAndCustomers', name: 'Sales by Customer', purpose: 'Invoiced sales in the range, by account.', filters: RANGE, waitsOn: null },
  { key: 'sales-by-item', category: 'SalesAndCustomers', name: 'Sales by Item', purpose: 'Invoiced sales in the range, by the item billed.', filters: RANGE, waitsOn: null },
  { key: 'sales-by-salesperson', category: 'SalesAndCustomers', name: 'Sales by Salesperson', purpose: 'Invoiced sales in the range, by the owner of the Deal the invoice belongs to.', filters: RANGE, waitsOn: null },
  { key: 'receivables-aging', category: 'SalesAndCustomers', name: 'Receivables Aging', purpose: 'What each account owes, by age, with a Disputed column of its own.', filters: ['asOf'], waitsOn: null },
  { key: 'customer-balances', category: 'SalesAndCustomers', name: 'Customer Balances', purpose: 'One balance per account: billed, paid and still owed.', filters: ['asOf'], waitsOn: null },
  { key: 'write-offs', category: 'SalesAndCustomers', name: 'Write-Offs by Reason and Authorizer', purpose: 'Every written-off invoice with the reason given and the person who authorized it.', filters: RANGE, waitsOn: null },
  {
    key: 'customer-statements', category: 'SalesAndCustomers', name: 'Customer Statements',
    purpose: 'One line per account: the latest statement sent and its closing balance, or the live balance where none was sent.', filters: AS_OF_AND_RANGE, rangeColumn: 'sentOn', waitsOn: null,
  },
  { key: 'credit-notes-pending-return', category: 'SalesAndCustomers', name: 'Credit Notes Awaiting Return', purpose: 'Notes sitting at Pending Return past the aging threshold, oldest first.', filters: ['asOf', 'dateRange', 'agingThresholdDays'], rangeColumn: 'creditDate', waitsOn: null },
  { key: 'return-bypasses', category: 'SalesAndCustomers', name: 'Return Bypasses by Reason and Approver', purpose: 'Every credit issued without the goods coming back, with the reason and the approver.', filters: RANGE, waitsOn: null },
  { key: 'level-mismatch', category: 'SalesAndCustomers', name: 'Level Mismatch', purpose: 'Every participation where the level sent differed from the level required, and what the difference billed.', filters: RANGE, waitsOn: null },
  { key: 'labour', category: 'SalesAndCustomers', name: 'Labor', purpose: 'Hours, cost, billed and unbilled labor in the range, by person, job or customer, with the hours quoted against the hours used.', filters: ['dateRange', 'groupBy'], waitsOn: null },

  // ---- Purchases and Expenses ----
  { key: 'expense-detail-by-category', category: 'PurchasesAndExpenses', name: 'Expense Detail by Category', purpose: 'Every approved expense line in the range, grouped by its category.', filters: RANGE, waitsOn: null },
  { key: 'expense-detail-by-project', category: 'PurchasesAndExpenses', name: 'Expense Detail by Project', purpose: 'Every approved expense line in the range, grouped by the Deal it was tagged to.', filters: RANGE, waitsOn: null },
  { key: 'payables-aging', category: 'PurchasesAndExpenses', name: 'Payables Aging', purpose: 'What is owed to each vendor, by age.', filters: ['asOf'], waitsOn: null },
  { key: 'vendor-balances', category: 'PurchasesAndExpenses', name: 'Vendor Balances', purpose: 'One balance per vendor: billed, paid and still owed.', filters: ['asOf'], waitsOn: null },
  { key: 'open-purchase-orders', category: 'PurchasesAndExpenses', name: 'Open Purchase Orders', purpose: 'Every order still open, with what has been received against it.', filters: AS_OF_AND_RANGE, rangeColumn: 'orderDate', waitsOn: null },

  // ---- Inventory and Assets ----
  { key: 'inventory-valuation', category: 'InventoryAndAssets', name: 'Inventory Valuation', purpose: 'What the stock on hand is worth, read off the cost walk rather than a stored figure.', filters: ['asOf', 'siteId'], waitsOn: null },
  { key: 'physical-stock-status', category: 'InventoryAndAssets', name: 'Physical Stock Status', purpose: 'On hand, claimed and available per item and site.', filters: ['siteId'], waitsOn: null },
  { key: 'inventory-adjustments-and-revaluations', category: 'InventoryAndAssets', name: 'Inventory Adjustments and Revaluations', purpose: 'Count variances and every revaluation in the range, with the reason each carried.', filters: RANGE, waitsOn: null },
  { key: 'low-stock', category: 'InventoryAndAssets', name: 'Low Stock', purpose: 'Every item and site under its own threshold, and the to-do each one raised.', filters: ['siteId'], waitsOn: null },
  { key: 'no-reorder-threshold', category: 'InventoryAndAssets', name: 'Items Without a Reorder Threshold', purpose: 'Every item and site carrying no reorder threshold, so a shortage there never raises a to-do.', filters: ['siteId'], waitsOn: null },
  { key: 'stale-allocations', category: 'InventoryAndAssets', name: 'Stale Allocations', purpose: 'Stock claimed and not moved for thirty days.', filters: AS_OF_AND_RANGE, rangeColumn: 'allocatedAt', waitsOn: null },
  { key: 'ordered-against-consumed', category: 'InventoryAndAssets', name: 'Ordered Against Consumed', purpose: 'Per Sales Order line, what was ordered against what has actually left.', filters: [], waitsOn: null },
  {
    key: 'asset-hierarchy-logs', category: 'InventoryAndAssets', name: 'Asset Hierarchy Logs',
    purpose: 'Every change to an asset hierarchy, with who made it.', filters: RANGE, waitsOn: null,
  },
  {
    key: 'capitalised-assets', category: 'InventoryAndAssets', name: 'Capitalized Assets with No Depreciation Schedule',
    purpose: 'Every owned asset carrying an acquisition cost and no schedule against it.', filters: ['asOf'], waitsOn: null,
  },

  // ---- Tax ----
  { key: 'sales-tax-liability', category: 'Tax', name: 'Sales Tax Liability', purpose: 'What each state liability account holds, by registration.', filters: ['asOf'], waitsOn: null },
  { key: 'sales-tax-summary', category: 'Tax', name: 'Sales Tax Summary', purpose: 'Taxable and exempt sales and the tax charged, by state, over the range.', filters: RANGE, waitsOn: null },
  { key: 'filings-due', category: 'Tax', name: 'Filings Due', purpose: 'Every registration and period with its due date, status, tax due and days late.', filters: AS_OF_AND_RANGE, rangeColumn: 'dueOn', waitsOn: null },
  { key: 'form-1099-summary', category: 'Tax', name: '1099 Summary by Vendor', purpose: 'One row per vendor: reportable payments for the year and the state of their return.', filters: ['taxYear'], waitsOn: null },
  { key: 'form-1099-detail', category: 'Tax', name: '1099 Transaction Detail', purpose: 'Every reportable payment behind the summary, card settlements excluded.', filters: ['taxYear'], waitsOn: null },
  // 🔒 Decision 9720 (T3164): the companies the Companies list's warning triangle marks, read through the one predicate.
  { key: 'vendors-missing-w9', category: 'Tax', name: 'Vendors Missing a W-9', purpose: 'Every vendor with no approved W-9 and no override: its W-9 status, the year’s reportable payments and whether payment is blocked.', filters: ['taxYear'], waitsOn: null },
  {
    key: 'customers-without-tax-exempt-certificate', category: 'Tax', name: 'Customers Without a Tax Exempt Certificate',
    purpose: 'Every customer with no certificate in force on any site, or whose last one expires within sixty days, with the site and the expiry.', filters: ['asOf'], waitsOn: null,
  },

  // ---- Pipeline ----
  { key: 'open-pipeline', category: 'Pipeline', name: 'Open Pipeline by Stage and Owner', purpose: 'Every open Deal by stage and owner, raw and weighted.', filters: RANGE, rangeColumn: 'expectedClose', waitsOn: null },
  { key: 'deals-by-expected-close-month', category: 'Pipeline', name: 'Deals by Expected Close Month', purpose: 'Open Deals grouped by the month they are expected to close.', filters: RANGE, waitsOn: null },
  { key: 'win-loss', category: 'Pipeline', name: 'Win and Loss', purpose: 'Won against lost in the range, losses grouped by competitor, with the aged unresolved count.', filters: RANGE, waitsOn: null },
];

export const REPORT_KEYS: readonly string[] = REPORTS.map((report) => report.key);

export function reportSpec(key: string): ReportSpec | null {
  return REPORTS.find((report) => report.key === key) ?? null;
}

/** The refusal a key nobody registered gets, so an unknown report is named rather than answering nothing. */
export function unknownReportRefusal(key: string): string {
  return `No report is registered under ${key}; the report set is a fixed list and nothing defines a new one.`;
}

export function reportsIn(category: ReportCategory): readonly ReportSpec[] {
  return REPORTS.filter((report) => report.category === category);
}

/**
 * 🔨 The prohibition, checked rather than trusted: nothing in the registry
 * offers a builder. A key, a name or a purpose carrying `custom`, `builder`,
 * `saved view` or `field picker` fails the suite (piece 26, No report builder).
 */
const BUILDER_WORDS = ['custom', 'customise', 'customize', 'builder', 'field picker', 'saved report', 'saved view', 'report designer'];

/**
 * 🔨 Matched on word boundaries, never as a substring: *customer* is the
 * subject of four of these reports and is not the word *custom*. A checker
 * that cannot tell them apart is a checker somebody turns off.
 */
export function builderWordIn(text: string): string | null {
  const lowered = text.toLowerCase();
  return BUILDER_WORDS.find((word) => new RegExp(`\\b${word}\\b`).test(lowered)) ?? null;
}

/** Every registry problem at once: a duplicate key, an unknown category, an unknown filter, a builder word. */
export function registryProblems(reports: readonly ReportSpec[] = REPORTS): readonly string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const report of reports) {
    if (seen.has(report.key)) problems.push(`two reports are registered under ${report.key}`);
    seen.add(report.key);
    if (!(REPORT_CATEGORIES as readonly string[]).includes(report.category)) problems.push(`${report.key} names no registered category`);
    for (const filter of report.filters) if (!(REPORT_FILTERS as readonly string[]).includes(filter)) problems.push(`${report.key} takes an unregistered filter ${filter}`);
    const word = builderWordIn(`${report.key} ${report.name} ${report.purpose}`);
    if (word) problems.push(`${report.key} carries the word ${word}, and no report builder is in scope`);
    const module = report.moduleKey;
    if (module !== null && module !== undefined && !isOptionalModuleKey(module)) problems.push(`${report.key} names the optional module ${module}, which is not one of the three`);
  }
  return problems;
}

// ---- the answer shape the browser reads ----

export type ReportCellKind = 'text' | 'money' | 'number' | 'percent' | 'date';

export interface ReportColumn {
  key: string;
  label: string;
  kind: ReportCellKind;
  /**
   * 🔒 True where the column is a cost or a margin figure (decision 0545). A
   * report's row is a generic map of values, so the column is the only place
   * that can say what a figure is, and the report set strips every flagged
   * column for a viewer holding no cost-and-margin capability.
   */
  costOrMargin?: boolean;
  /** 🔒 False where the column's header offers no sort (decision 10062): a drilled account's running Balance. */
  sortable?: boolean;
}

export interface ReportRow {
  values: Readonly<Record<string, string | number | null>>;
  /** The page the row drills through to, relative to the browser's own root; null where the row names no record. */
  link: string | null;
  /** The heading the row sits under, where the report groups; null where it does not. */
  group: string | null;
  /**
   * How far the row's first cell stands in (decision 0763): a sub-account under
   * the account it rolls up to, a line under a sub-heading. Absent is zero.
   */
  indent?: number;
  /**
   * 🔒 What the row is (decision 0880): a heading, an account, a total over the
   * rows above it, or a result a reader came for. Absent reads as an account, so
   * an older answer still draws. The page, the sheet and the CSV each draw one
   * kind one way.
   */
  kind?: ReportRowKind;
  /**
   * 🔒 The figure columns whose value carries a mark after it (decision 1140):
   * a credited cost line's *(credit)*, keyed by column. Absent is none. The page
   * and the printed sheet draw it; the CSV carries the number alone.
   */
  marks?: Readonly<Record<string, ReportCellMark>>;
  /**
   * 🔒 The record a tick on the row names, on a report whose rows carry an act (decision 11040, amending 1002 for that
   * report alone): the Low Stock row's `ItemSiteStock` id, which Raise Purchase Orders reads. Absent, the row takes no tick.
   */
  selectId?: string;
}

/** The one mark a report cell carries today: a credit inside a cost or expense section (decision 1140). */
export type ReportCellMark = 'credit';

/**
 * 🔒 The four kinds of report row (decision 0880): a heading is bold and carries
 * no figure; an account stands in under its heading; a total is bold over a
 * single rule; a result is bold between a single and a double rule, with a gap
 * after it.
 */
export type ReportRowKind = 'heading' | 'account' | 'total' | 'result';

/** 🔒 A row's kind as every renderer reads it: an account where the row names none (decision 0880). */
export function reportRowKind(row: Pick<ReportRow, 'kind'>): ReportRowKind {
  return row.kind ?? 'account';
}

/**
 * 🔒 A row that sorts nowhere (decision 0880): a heading, a total or a result
 * stands where its report put it, so a report carrying one offers no sort.
 */
export function reportRowsAreOrdered(rows: readonly Pick<ReportRow, 'kind'>[]): boolean {
  return rows.some((row) => reportRowKind(row) !== 'account');
}

/**
 * 🔒 A result that names its own group opens no band above itself (decision
 * 0880): Gross Profit is a group of one row reading Gross Profit, so a band
 * would print the name twice. A result closing a section of accounts — Net cash
 * provided by Investing Activities under its own heading — keeps the band.
 */
export function reportRowNamesItsGroup(row: Pick<ReportRow, 'kind' | 'group' | 'values'>, firstColumnKey: string | undefined): boolean {
  return reportRowKind(row) === 'result' && firstColumnKey !== undefined && row.group !== null && row.values[firstColumnKey] === row.group;
}

/** 🔒 A row's first cell as it is drawn, standing in by its indent (decision 0763); a non-breaking space, so neither the screen nor the paper collapses it. */
export function indentedText(text: string, indent: number | undefined): string {
  return indent && indent > 0 ? `${'\u00a0'.repeat(4 * indent)}${text}` : text;
}

/** One label-value fact printed in the on-screen header and again at the head of the PDF. */
export interface ReportFact {
  label: string;
  /** The fact as text; a money fact's is the shared formatter's own writing of `cents`. */
  value: string;
  /**
   * 🔒 The figure in cents where the fact is money (decision 0764). The screen
   * draws it through the Money component and the CSV writes it as decimal dollars,
   * so no statement total prints its raw cents (decision 0564).
   */
  cents?: number;
}

/**
 * 🔒 A money fact (decision 0764): the figure in cents and its text through the
 * one shared formatter. 💰 The statements handed a fact `String(cents)`, so the
 * Profit and Loss read *Net result 40000* over a table reading $400.00.
 */
export function moneyFact(label: string, cents: number, currencyCode = 'USD'): ReportFact {
  return { label, value: formatCurrency({ amount: cents, currencyCode }), cents };
}

export interface ReportAnswer {
  key: string;
  name: string;
  category: ReportCategory;
  purpose: string;
  /** 🔒 The basis, cash or accrual, on the face of every financial statement; null where the report is not a statement. */
  basis: LedgerBasis | null;
  /** The sentence the basis prints as, drawn in the header and in the PDF. */
  basisHeader: string | null;
  facts: readonly ReportFact[];
  columns: readonly ReportColumn[];
  rows: readonly ReportRow[];
  /** The figure per column the report totals, keyed by column; empty where nothing totals. */
  totals: Readonly<Record<string, number>>;
  /**
   * 🔒 The kind the foot is drawn in, as the table's last row (decision 0880):
   * a total where absent, and a result on the Trial Balance and the Journal,
   * whose two agreeing totals are the reason each exists.
   */
  totalsKind?: 'total' | 'result';
  /** What the report could not answer, named on its own face rather than dropped. */
  notices: readonly string[];
  /** The sentence naming the piece that makes the report runnable, where it is not yet. */
  waitsOn: string | null;
  /** True where the report groups its rows under headings. */
  grouped: boolean;
  /** 🔒 The one account General Ledger detail was narrowed to (decision 9620): its id and its number and name, for the filter row. */
  accountFilter?: { id: string; label: string };
  /**
   * 🔒 The organization's fiscal year start, on a statement taking the Customize Report panel (T2751): the
   * filter bar resolves a date preset to its From and To against it, as the server does.
   */
  fiscalAnchor?: FiscalAnchor;
  /**
   * 🔒 The organization's data-coverage date as a calendar day (piece 31; decision 9200, row T2369): the earliest
   * day the imported data covers. The sheet warns when the report's range opens before it.
   */
  coverageFrom?: string;
  /**
   * 🔒 The day the report's range opens, as a calendar day: the From a person typed (T2784), else the day a
   * statement's Customize Report panel resolved. Absent on a report run with no range asked for.
   */
  rangeFrom?: string;
  /**
   * 🔒 The report's sheet as the printer's own HTML, with its page count and its
   * page, which the report page's PDF view frames (T350; decision 1003). It rides
   * the report's own answer, run on the same filters, so the view is instant and
   * never a second fetch; the CSV and the PDF export carry none.
   */
  printSheet?: ReportPrintSheet | null;
}

/**
 * 🔒 The data-coverage date a report's range opens before (piece 31; decision 9200), or null: the sheet draws its
 * warning only where both days are known and the range's first day is earlier than the coverage date.
 */
export function coverageShortfall(answer: Pick<ReportAnswer, 'coverageFrom' | 'rangeFrom'>): string | null {
  const { coverageFrom, rangeFrom } = answer;
  return coverageFrom && rangeFrom && rangeFrom < coverageFrom ? coverageFrom : null;
}

/** 🔒 A report's sheet as the PDF view frames it (decision 1003): the HTML, how many pages it holds, and the page's size in inches. */
export interface ReportPrintSheet {
  html: string;
  pageCount: number;
  page: { widthInches: number; heightInches: number };
}

export interface ReportsListAnswer {
  categories: readonly { category: ReportCategory; label: string; reports: readonly ReportSpec[] }[];
  /** 🔨 Withheld at the data layer: false where the viewer holds no cost-and-margin capability (piece 26). */
  seesCostAndMargin: boolean;
}

/**
 * The list answer, grouped in the registry's own order so the screen chooses no
 * arrangement of its own. 🔨 A caller that has read no module switch is holding
 * all three off, which is the default the piece states, so a module's report is
 * absent from the list until somebody has said the module is on.
 */
export function reportsList(seesCostAndMargin: boolean, modules: ModuleStates = ALL_MODULES_OFF): ReportsListAnswer {
  return {
    categories: REPORT_CATEGORIES.map((category) => ({
      category,
      label: REPORT_CATEGORY_LABELS[category],
      reports: reportsIn(category).filter((report) => moduleRowVisible(report, modules)),
    })),
    seesCostAndMargin,
  };
}

// ---- Cost and margin, withheld column by column ----

/** The notice a stripped report carries, so a missing column is stated rather than looking like a report with fewer figures. */
export const COST_COLUMNS_WITHHELD = 'Cost and margin figures are withheld from this report, because this login holds no cost-and-margin grant.';

/**
 * 🔒 The notices that talk about a cost figure (T1320): a report that carries one and loses its cost column drops the notice too, because it points at a column the viewer cannot see.
 */
export const COST_ONLY_NOTICES: readonly string[] = ['An entry whose worker has no pay rate on its day costs nothing here.'];

/**
 * 🔒 The answer with every cost-or-margin column removed — the column, the cell
 * under it in every row, and its total (decision 0545). 🔨 The cell goes too: a
 * value the browser received is a value the browser can be made to draw, and a
 * report row is a map, so dropping the column alone would ship the figure.
 */
export function withoutCostColumns(answer: ReportAnswer): ReportAnswer {
  const withheld = answer.columns.filter((column) => column.costOrMargin === true).map((column) => column.key);
  if (withheld.length === 0) return answer;
  const keep = (values: Readonly<Record<string, string | number | null>>) =>
    Object.fromEntries(Object.entries(values).filter(([key]) => !withheld.includes(key)));
  return {
    ...answer,
    columns: answer.columns.filter((column) => column.costOrMargin !== true),
    rows: answer.rows.map((row) => ({ ...row, values: keep(row.values) })),
    totals: Object.fromEntries(Object.entries(answer.totals).filter(([key]) => !withheld.includes(key))),
    notices: [...answer.notices.filter((notice) => !COST_ONLY_NOTICES.includes(notice)), COST_COLUMNS_WITHHELD],
  };
}

// ---- CSV, the secondary export ----

/**
 * 🔨 A cell whose text opens with `=`, `+`, `-`, `@`, a tab or a carriage return is prefixed with an
 * apostrophe, because a spreadsheet reads it as a formula otherwise and an
 * exported report becomes an instruction (found in review, 2026-09-18). A number
 * is never prefixed, so a negative figure stays a figure.
 *
 * 🔨 A carriage return is quoted beside a comma, a quote and a newline: an
 * unquoted one splits the row in two and the halves read as data.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** 🔨 A signed figure written out as text is a figure, not a formula, so `-12.50` crosses untouched. */
const PLAIN_NUMBER = /^[-+]?\d+(\.\d+)?$/;

export function csvCell(value: string | number | null): string {
  if (value === null) return '';
  if (typeof value === 'number') return String(value);
  const text = FORMULA_LEAD.test(value) && !PLAIN_NUMBER.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * 🔒 A money cell in a CSV is decimal dollars, never integer cents (decision
 * 0564). A spreadsheet adds the column somebody opened it to add, so `301.00`
 * is the figure and `30100` is a hundredfold error waiting to be pasted into a
 * board pack. The symbol is left off, because a cell carrying one stops being a
 * number the sheet can sum.
 */
export function csvFigure(value: string | number | null, kind: ReportCellKind): string {
  if (kind !== 'money' || typeof value !== 'number') return csvCell(value);
  return csvCell((value / 100).toFixed(2));
}

/**
 * The CSV the secondary action downloads. 🔨 It carries the same header facts
 * the screen and the PDF print, because a figure without its basis and its
 * range is not evidence of anything.
 */
export function reportCsv(answer: ReportAnswer): string {
  const lines: string[] = [csvCell(answer.name)];
  if (answer.basisHeader) lines.push(csvCell(answer.basisHeader));
  // 🔒 A money fact is decimal dollars in the CSV, like every money cell (decision 0564).
  for (const fact of answer.facts) lines.push([csvCell(fact.label), fact.cents === undefined ? csvCell(fact.value) : csvFigure(fact.cents, 'money')].join(','));
  for (const notice of answer.notices) lines.push(csvCell(notice));
  lines.push('');
  const columns = answer.grouped ? [{ key: '__group', label: 'Group', kind: 'text' as ReportCellKind }, ...answer.columns] : answer.columns;
  lines.push(columns.map((column) => csvCell(column.label)).join(','));
  for (const row of answer.rows) {
    lines.push(columns.map((column) => (column.key === '__group' ? csvCell(row.group) : csvFigure(row.values[column.key] ?? null, column.kind))).join(','));
  }
  if (Object.keys(answer.totals).length > 0) {
    lines.push(columns.map((column, index) => (index === 0 ? csvCell('Total') : csvFigure(answer.totals[column.key] ?? null, column.kind))).join(','));
  }
  return `${lines.join('\n')}\n`;
}

/** The download's own name, so the CSV and the PDF land beside each other in a downloads folder. */
export function reportFilename(key: string, extension: 'csv' | 'pdf', on: Date): string {
  return `${key}-${on.toISOString().slice(0, 10)}.${extension}`;
}
